import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, Users, Sparkles, Crown, ArrowRight } from "lucide-react";

/**
 * EventMatchStrip — Hooked-style social proof strip on every event page.
 * Shows how many verified singles are attending, likes/matches so far, and a
 * "See who's going and match before you meet" CTA.  Fully gated: fans without
 * a ticket see a "Book to unlock" pill, non-VIP fans see "Apply for VIP" CTA.
 */
export default function EventMatchStrip({ event }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!event?.id || !user) { setLoading(false); return; }
    api.get(`/clubvip/event-stats/${event.id}`)
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [event?.id, user]);

  // Guest / logged-out — teaser card
  if (!user) {
    return (
      <div className="glass rounded-3xl p-5 relative overflow-hidden" data-testid="event-match-strip-guest">
        <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(600px 200px at 90% 0%, rgba(255,46,154,0.35), transparent)" }} />
        <div className="relative flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-magenta/20 text-magenta flex items-center justify-center"><Heart className="w-5 h-5" fill="currentColor" /></div>
            <div>
              <div className="font-heading font-bold text-sm">See who's going.</div>
              <div className="text-[11px] text-muted">Match before you meet — ClubVIP is exclusive to approved members going to this event.</div>
            </div>
          </div>
          <Link to="/login" data-testid="match-strip-login" className="btn-pink !py-2 !px-4 !text-xs ml-auto">Login to preview</Link>
        </div>
      </div>
    );
  }

  if (loading || !stats) return null;

  // Fan without a ticket
  if (!stats.my_ticket) {
    return (
      <div className="glass rounded-3xl p-5 relative overflow-hidden" data-testid="event-match-strip-no-ticket">
        <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(600px 200px at 0% 0%, rgba(212,255,0,0.25), transparent)" }} />
        <div className="relative flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-lime/20 text-lime flex items-center justify-center"><Users className="w-5 h-5" /></div>
            <div>
              <div className="font-heading font-bold text-sm">
                {stats.verified_singles > 0 ? `${stats.verified_singles} verified singles going · ${stats.matches} matches so far` : "Book your ticket to unlock the crowd"}
              </div>
              <div className="text-[11px] text-muted">Grab a ticket and ClubVIP unlocks — see who else is going, match before you meet.</div>
            </div>
          </div>
          <a href="#book" data-testid="match-strip-book" className="btn-lime !py-2 !px-4 !text-xs ml-auto">Book to unlock →</a>
        </div>
      </div>
    );
  }

  // Ticket-holder, not VIP
  if (!user.vip) {
    return (
      <div className="glass rounded-3xl p-5 relative overflow-hidden" data-testid="event-match-strip-not-vip">
        <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(600px 200px at 90% 0%, rgba(123,47,255,0.35), transparent)" }} />
        <div className="relative flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-purple/20 text-purple flex items-center justify-center"><Crown className="w-5 h-5" /></div>
            <div>
              <div className="font-heading font-bold text-sm">{stats.verified_singles} verified singles going · {stats.matches} matches</div>
              <div className="text-[11px] text-muted">Apply for VIP + complete your ClubVIP profile to see who's attending this event.</div>
            </div>
          </div>
          <Link to="/vip" data-testid="match-strip-vip" className="btn-purple !py-2 !px-4 !text-xs ml-auto">Apply for VIP →</Link>
        </div>
      </div>
    );
  }

  // Approved VIP — full deck link
  return (
    <div className="glass rounded-3xl p-5 relative overflow-hidden" data-testid="event-match-strip-active">
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(600px 220px at 90% 0%, rgba(255,46,154,0.35), transparent), radial-gradient(600px 220px at 0% 100%, rgba(212,255,0,0.25), transparent)" }} />
      <div className="relative">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-lime" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-lime">ClubVIP · This Event</div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 max-w-md">
          <div>
            <div className="font-fancy text-3xl text-magenta leading-none">{stats.verified_singles}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted mt-1">singles going</div>
          </div>
          <div>
            <div className="font-fancy text-3xl text-lime leading-none">{stats.likes}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted mt-1">likes sent</div>
          </div>
          <div>
            <div className="font-fancy text-3xl text-skyx leading-none">{stats.matches}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted mt-1">matches made</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to={`/clubvip?event=${event.id}`} data-testid="match-strip-open-deck" className="btn-pink !py-2 !px-4 !text-xs">
            <Heart className="w-3.5 h-3.5" fill="currentColor" /> See who's going
          </Link>
          <Link to="/clubvip" data-testid="match-strip-all" className="btn-ghost !py-2 !px-4 !text-xs">All members <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <div className="text-[10px] font-mono text-muted mt-3 max-w-md">Private-by-default. Only first name + blurred photo + top 3 interests are visible until you both hit Love Match.</div>
      </div>
    </div>
  );
}
